import { useEffect, useState } from 'react';
import { Modal, Space, Form, Input, Flex, Button, Image, message, Popconfirm, Empty, Switch, Tag } from 'antd';
import MultiImagePicker from 'components/pickers/image/MultiImagePicker';
import OrderService from 'services/order';
import { formatDate } from 'utils/date';
import { useAuth } from 'contexts/auth';

const service = new OrderService();

const OrderNoteModal = ({ orderID, open, onClose }) => {
    const { user } = useAuth();
    const [orderNotes, setOrderNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [savingNote, setSavingNote] = useState(false);
    const [editingNoteID, setEditingNoteID] = useState(null);
    const [editingShowToCustomer, setEditingShowToCustomer] = useState(false);
    const [noteForm] = Form.useForm();

    useEffect(() => {
        if (open && orderID) {
            loadOrderNotes();
        }
    }, [open, orderID]);

    const loadOrderNotes = async () => {
        if (!orderID) {
            return;
        }
        setLoadingNotes(true);
        try {
            const notes = await service.getNotes({ id: orderID });
            setOrderNotes(Array.isArray(notes) ? notes : []);
        } finally {
            setLoadingNotes(false);
        }
    };

    const resetForm = () => {
        setEditingNoteID(null);
        setEditingShowToCustomer(false);
        noteForm.resetFields();
    };

    const onCreateOrUpdateOrderNote = async (values) => {
        const content = String(values?.content || '').trim();
        const images = Array.isArray(values?.images) ? values.images : [];
        const show_to_customer = editingNoteID ? editingShowToCustomer : false;
        if (!content && images.length === 0) {
            message.error('Vui lòng nhập nội dung hoặc đính kèm hình ảnh');
            return;
        }

        setSavingNote(true);
        try {
            if (editingNoteID) {
                await service.updateNote({ id: orderID, note_id: editingNoteID, content, images, show_to_customer });
                message.success('Đã cập nhật ghi chú');
            } else {
                await service.addNote({ id: orderID, content, images, show_to_customer });
                message.success('Đã thêm ghi chú');
            }

            resetForm();
            await loadOrderNotes();
        } finally {
            setSavingNote(false);
        }
    };

    const onEditNote = (note) => {
        setEditingNoteID(note?.id);
        setEditingShowToCustomer(!!note?.show_to_customer);
        noteForm.setFieldsValue({
            content: note?.content || '',
            images: Array.isArray(note?.images) ? note.images : []
        });
    };

    const onToggleNoteVisibility = async (note, checked) => {
        setSavingNote(true);
        try {
            await service.updateNote({
                id: orderID,
                note_id: note?.id,
                content: String(note?.content || '').trim(),
                images: Array.isArray(note?.images) ? note.images : [],
                show_to_customer: !!checked
            });

            setOrderNotes((prev) => (prev || []).map((x) => (
                x?.id == note?.id ? { ...x, show_to_customer: !!checked } : x
            )));

            if (editingNoteID == note?.id) {
                setEditingShowToCustomer(!!checked);
            }

            message.success(checked ? 'Đã bật hiển thị cho khách hàng' : 'Đã ẩn khỏi khách hàng');
        } finally {
            setSavingNote(false);
        }
    };

    const onDeleteNote = async (noteID) => {
        setSavingNote(true);
        try {
            await service.deleteNote({ id: orderID, note_id: noteID });
            message.success('Đã xoá ghi chú');
            if (editingNoteID == noteID) {
                resetForm();
            }
            await loadOrderNotes();
        } finally {
            setSavingNote(false);
        }
    };

    return (
        <Modal
            title='Ghi chú đơn hàng'
            open={open}
            onCancel={onClose}
            footer={null}
            width={760}
        >
            <Space direction='vertical' style={{ width: '100%' }}>


                <div style={{ maxHeight: 420, overflowY: 'auto', padding: '8px 4px' }}>
                    {loadingNotes && <div>Đang tải...</div>}

                    {!loadingNotes && (!orderNotes || orderNotes.length === 0) && (
                        <Empty description='Chưa có ghi chú nào' />
                    )}

                    {!loadingNotes && [...(orderNotes || [])].reverse().map(note => {
                        const isMe = String(note?.create_by?.id || '') === String(user?.id || '');
                        const isEditingThisNote = editingNoteID == note?.id;
                        return (
                            <div key={note?.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                                <div
                                    style={{
                                        width: '100%',
                                        maxWidth: '82%',
                                        background: isMe ? '#e6f4ff' : '#f5f5f5',
                                        borderRadius: 12,
                                        padding: 12,
                                        border: '1px solid #f0f0f0'
                                    }}
                                >
                                    <Flex justify='space-between' align='flex-start' style={{ marginBottom: 8 }}>
                                        <Space direction='vertical' size={2}>
                                            <b>{note?.create_by?.full_name || 'Người dùng'}</b>
                                            {isEditingThisNote && <Tag color='processing' style={{ width: 'fit-content' }}>Đang chỉnh sửa</Tag>}
                                        </Space>
                                        <Space size={4}>
                                            <Button size='small' type='text' onClick={() => onEditNote(note)}>Sửa</Button>
                                            <Popconfirm
                                                title='Xoá ghi chú'
                                                description='Bạn có chắc muốn xoá ghi chú này?'
                                                onConfirm={() => onDeleteNote(note?.id)}
                                                okText='Xoá'
                                                cancelText='Huỷ'
                                            >
                                                <Button size='small' type='text' danger>Xoá</Button>
                                            </Popconfirm>
                                        </Space>
                                    </Flex>

                                    {!!note?.content && <div style={{ marginBottom: note?.images?.length ? 10 : 0 }}>{note.content}</div>}

                                    {Array.isArray(note?.images) && note.images.length > 0 && (
                                        <Space wrap style={{ marginBottom: 10 }}>
                                            {note.images.map((img, idx) => (
                                                <Image key={`${note.id || 'note'}-${idx}`} width={88} src={img?.url} />
                                            ))}
                                        </Space>
                                    )}

                                    <Flex
                                        justify='space-between'
                                        align='center'
                                        style={{
                                            background: '#ffffff',
                                            border: '1px dashed #d9d9d9',
                                            borderRadius: 8,
                                            padding: '6px 8px'
                                        }}
                                    >
                                        <Space size={6}>
                                            <span style={{ fontSize: 12, color: '#595959' }}>Hiển thị cho khách hàng</span>
                                            <Tag color={note?.show_to_customer ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
                                                {note?.show_to_customer ? 'Đang hiện' : 'Đang ẩn'}
                                            </Tag>
                                        </Space>
                                        <Switch
                                            size='small'
                                            checked={!!note?.show_to_customer}
                                            disabled={savingNote}
                                            checkedChildren='Hiện'
                                            unCheckedChildren='Ẩn'
                                            onChange={(checked) => onToggleNoteVisibility(note, checked)}
                                        />
                                    </Flex>

                                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>{formatDate(note.create_on)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <Form form={noteForm} layout='vertical' onFinish={onCreateOrUpdateOrderNote}>
                    <Form.Item label='Nội dung' name='content'>
                        <Input.TextArea rows={3} placeholder='Nhập ghi chú...' />
                    </Form.Item>
                    <Form.Item label='Hình ảnh' name='images'>
                        <MultiImagePicker maxPhotos={5} />
                    </Form.Item>
                    <Flex justify='space-between'>
                        <div>
                            {editingNoteID && (
                                <Button onClick={resetForm}>Huỷ sửa</Button>
                            )}
                        </div>
                        <Button type='primary' htmlType='submit' loading={savingNote}>
                            {editingNoteID ? 'Cập nhật ghi chú' : 'Lưu ghi chú'}
                        </Button>
                    </Flex>
                </Form>
            </Space>
        </Modal>
    );
};

export default OrderNoteModal;
